'use client';

import { getAddressLabels } from '@ahm/api-wrappers-core';
import { useLocationStore } from '@ahm/react-map/store';
import { usePathname, useRouter } from 'next/navigation';
import Script from 'next/script';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { getServiceType } from '@/lib/utils';
import { useBookingStore } from '@/stores/booking/booking-store-provider';

interface ChatInitializerProps {
  isAuthenticated: boolean;
  userToken: string | null;
  theme?: 'light' | 'dark';
  className?: string;
}

export default function ChatInitializer({
  isAuthenticated,
  userToken,
  theme = 'light',
  className,
}: ChatInitializerProps) {
  const [shouldInitChat, setShouldInitChat] = useState(false);
  const router = useRouter();
  const pathname = usePathname();

  const { isBikeService } = useMemo(() => getServiceType(pathname), [pathname]);
  const setPaths = useLocationStore.use.setPaths();
  const paths = useLocationStore.use.paths();
  const {
    bikePackageWeight,
    bikePackageSize,
    setEstimatedData,
    setBikePackageSize,
    setBikePackageWeight,
    serviceTypes,
    setSelectedService,
    setPromoCode,
  } = useBookingStore((state) => state);

  useEffect(() => {
    if (isAuthenticated && userToken) {
      setShouldInitChat(true);
    } else {
      setShouldInitChat(false);
    }
  }, [isAuthenticated, userToken]);

  // Helper function to validate and process path data
  const processPathData = useCallback(
    (paths: any) => {
      const pathsData = [
        {
          ...paths[0],
          first_label: getAddressLabels(paths[0].address),
          second_label: getAddressLabels(paths[0].address),
          mobile: paths[0].phone,
        },
        {
          ...paths[1],
          first_label: getAddressLabels(paths[1].address),
          second_label: getAddressLabels(paths[1].address),
          mobile: paths[1].phone,
        },
      ];
      if (pathsData && Array.isArray(pathsData) && pathsData.length > 0) {
        setPaths(pathsData);
      }
    },
    [setPaths]
  );

  // Helper function to validate and process service data
  const processServiceData = useCallback(
    (service_id: any) => {
      if (
        service_id &&
        serviceTypes &&
        Array.isArray(serviceTypes) &&
        serviceTypes.length > 0
      ) {
        const selected = serviceTypes.find((s) => s && s._id === service_id);
        if (selected) {
          setSelectedService(selected);
        }
      }
    },
    [serviceTypes, setSelectedService]
  );

  // Helper function to validate and process package info
  const processPackageInfo = useCallback(
    (packageInfo: any) => {
      if (packageInfo) {
        if (packageInfo.package_size !== undefined) {
          setBikePackageSize(packageInfo.package_size);
        }
        if (packageInfo.weight_kg !== undefined) {
          setBikePackageWeight(packageInfo.weight_kg);
        }
      }
    },
    [setBikePackageSize, setBikePackageWeight]
  );

  // Helper function to handle syncEstimatedData
  const handleSyncEstimatedData = useCallback(
    (payload: any) => {
      try {
        // Validate payload structure
        if (!payload || !Array.isArray(payload) || payload.length === 0) {
          console.warn('Invalid payload for syncEstimatedData:', payload);
          return;
        }
        console.log('syncEstimatedData received', payload[0]);
        if (!payload[0].dataEstimate) {
          console.warn('No estimate data in payload:', payload);
          return;
        }
        const estimateData = payload[0].dataEstimate;

        // Validate estimateData exists
        if (!estimateData || typeof estimateData !== 'object') {
          console.warn('Invalid estimateData:', estimateData);
          return;
        }

        const packageInfo = {
          package_size: payload[0].params.package_size,
          weight_kg: payload[0].params.package_weight,
        };
        // Process different parts of the data
        processPathData(payload[0].params.path);
        processServiceData(payload[0].params.service_id);
        processPackageInfo(packageInfo);

        // Handle estimated data
        setEstimatedData({
          service_id: payload[0].params.service_id || '',
          data: estimateData || null,
        });
        if (payload[0].dataEstimate.suggested_promotion) {
          router.replace(
            '/?promo_code=' + payload[0].dataEstimate.suggested_promotion
          );
          setPromoCode(payload[0].dataEstimate.suggested_promotion);
        }
      } catch (error) {
        console.error('Error handling syncEstimatedData:', error);
      }
    },
    [processPathData, processServiceData, processPackageInfo, setPromoCode]
  );

  // Helper function to handle openPage
  const handleOpenPage = useCallback(
    (payload: any) => {
      try {
        // Validate payload structure
        if (!payload || !Array.isArray(payload) || payload.length === 0) {
          console.warn('Invalid payload for openPage:', payload);
          return;
        }

        const targetPath = payload[0];
        if (targetPath && typeof targetPath === 'string') {
          router.push(`${process.env.NEXT_PUBLIC_APP_V1_URI}${targetPath}`);
        }
      } catch (error) {
        console.error('Error handling openPage:', error);
      }
    },
    [router]
  );

  // Helper function to handle syncOrderData
  // const handleSyncOrderData = useCallback((payload: any) => {
  //   try {
  //     console.log('syncOrderData received', payload);
  //     // Add any additional logic here
  //   } catch (error) {
  //     console.error('Error handling syncOrderData:', error);
  //   }
  // }, []);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      try {
        // Validate event and event.data exist
        if (!event?.data) {
          return;
        }

        const { type, payload } = event.data;

        // Validate type exists and is string
        if (!type || typeof type !== 'string') {
          return;
        }

        switch (type) {
          case 'syncEstimatedData': {
            handleSyncEstimatedData(payload);
            break;
          }
          case 'openPage': {
            console.log('openPage received', payload);
            handleOpenPage(payload);
            break;
          }
          case 'syncOrderData': {
            // router.replace('/');
            // setEstimatedData(null);
            // handleSyncOrderData(payload);
            setEstimatedData(null);
            setBikePackageSize('S');
            setBikePackageWeight(undefined);
            setPromoCode(null);
            break;
          }

          default: {
            // console.log('Unknown message type:', type);
            break;
          }
        }
      } catch (error) {
        console.error(
          'Error in handleMessage:',
          error,
          'Event data:',
          event?.data
        );
      }
    };

    if (shouldInitChat) {
      window.addEventListener('message', handleMessage);
    }

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [
    bikePackageSize,
    bikePackageWeight,
    paths,
    shouldInitChat,
    handleSyncEstimatedData,
    setEstimatedData,
    setBikePackageSize,
    setBikePackageWeight,
    setPromoCode,
  ]);

  if (!isBikeService) {
    return null;
  }

  return (
    <div className={className}>
      <Script
        // src="http://localhost:3001/main.0.0.1.js"
        src={`${process.env.NEXT_PUBLIC_CHAT2BOOK_EMBED_URL}?v=${Date.now()}`}
        // strategy="afterInteractive"
      />

      {/* Only render init script when user is authenticated */}
      {shouldInitChat && userToken && (
        <Script
          id="chat-init"
          strategy="afterInteractive"
          dangerouslySetInnerHTML={{
            __html: `
              function initChatWhenReady() {
                if (window.Chat) {
                  Chat.init({
                    theme: '${theme}',
                    token: '${userToken}',
                    // Add any additional config here
                    enableAutoFocus: true,
                    enableSounds: true,

                  });
                } else {
                  setTimeout(initChatWhenReady, 100);
                }
              }
              initChatWhenReady();
            `,
          }}
        />
      )}
    </div>
  );
}
